import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'

import { PlataEnJuego } from './PlataEnJuego'
import { DecisionesRapidas } from './DecisionesRapidas'
import { DondeEstaElEquipo } from './DondeEstaElEquipo'
import { CifrasDelPeriodo } from './periodo/CifrasDelPeriodo'
import { esperaDecision } from './tiposDeDecision'
import { duracion } from './duracion'
import { EquipoEnCifras } from './EquipoEnCifras'
import { Indicador } from './Indicador'
import { useLoQueEspera } from './useLoQueEspera'
import { HORAS_ALARMA, HORAS_AVISO, useIndicadoresCotizacion } from './useTablero'
import estilos from './PaginaTablero.module.css'

/**
 * El tablero.
 *
 * Dos preguntas: qué está quieto y cómo vamos contra lo que prometimos. Todo
 * sale de lo que el recorrido ya registró; no se digita nada aquí.
 */
export function PaginaTablero() {
  const { indicadores, cargando, error } = useIndicadoresCotizacion()
  const { pendientes } = useLoQueEspera()

  // Lo que no se decide con un sí o un no se cuenta, y se atiende en la
  // matriz: duplicar aquí la lista de la matriz no le ahorra trabajo a nadie.
  const porProgramar = pendientes.filter((p) => p.tipo === 'programar')
  // En precierre también están las que piden más horas: siguen en precierre
  // esperando una decisión. Antes no se contaban y salía «0» con una orden
  // ahí (la OT-2026-0048 de Macroproyectos).
  const porCerrar = pendientes.filter((p) => p.tipo === 'precierre' || p.tipo === 'mas_tiempo')

  const sinProgramar = porProgramar.length
  const enPrecierre = porCerrar.length

  /**
   * EL COLOR SE GANA CON EL TIEMPO, NO CON LA EXISTENCIA.
   */
  function tonoPorEspera(cola: typeof pendientes): 'neutro' | 'aviso' | 'alarma' {
    if (cola.length === 0) return 'neutro'

    const peor = Math.max(...cola.map((p) => p.horas_esperando))

    if (peor >= HORAS_ALARMA) return 'alarma'
    if (peor >= HORAS_AVISO) return 'aviso'

    return 'neutro'
  }

  const hayAlgoPendiente = pendientes.some((p) => esperaDecision(p.tipo))

  const hayQueDecidir = pendientes.some((p) =>
    ['aprobar_cotizacion', 'mas_tiempo', 'inicio_sin_ubicacion'].includes(p.tipo),
  )

  if (cargando) return <Cargando mensaje="CALCULANDO INDICADORES CLAVE..." />

  if (error) {
    return <Aviso tipo="error">{error}</Aviso>
  }

  if (!indicadores) {
    return <Aviso tipo="info">Todavía no hay órdenes registradas.</Aviso>
  }

  const {
    sin_cotizar,
    en_aviso,
    en_alarma,
    espera_maxima_horas,
    mas_vieja_numero,
    cotizadas,
    demora_mediana_horas,
    demora_maxima_horas,
    cliente_mediana_dias,
    total_ordenes,
  } = indicadores

  const sinHistoria = cotizadas > 0 && (demora_maxima_horas ?? 0) < 0.5

  return (
    <div className={estilos.pagina}>
      <PlataEnJuego />

      <div className={estilos.indicadores}>
        <Indicador
          rotulo="Sin cotizar"
          valor={String(sin_cotizar)}
          alDia={sin_cotizar === 0}
          tono={
            en_alarma > 0 ? 'alarma' : en_aviso > 0 ? 'aviso' : sin_cotizar === 0 ? 'ok' : 'neutro'
          }
          detalle={
            sin_cotizar === 0
              ? `Las ${total_ordenes} tienen precio`
              : `de ${total_ordenes} órdenes · ${en_alarma} pasan de ${HORAS_ALARMA / 24} días`
          }
        />

        <Indicador
          rotulo="La que más espera"
          valor={duracion(espera_maxima_horas)}
          alDia={espera_maxima_horas == null}
          tono={
            (espera_maxima_horas ?? 0) >= HORAS_ALARMA
              ? 'alarma'
              : (espera_maxima_horas ?? 0) >= HORAS_AVISO
              ? 'aviso'
              : 'neutro'
          }
          detalle={mas_vieja_numero ?? 'Ninguna esperando'}
        />

        <Indicador
          rotulo="Sin programar"
          valor={String(sinProgramar)}
          alDia={sinProgramar === 0}
          tono={sinProgramar === 0 ? 'ok' : tonoPorEspera(porProgramar)}
          detalle={
            sinProgramar > 0
              ? 'Aprobadas y sin colaborador ni fecha · se programa en la matriz'
              : 'Todo lo aprobado tiene fecha'
          }
        />

        <Indicador
          rotulo="De orden a cotización"
          valor={sinHistoria ? null : duracion(demora_mediana_horas)}
          detalle={
            sinHistoria
              ? `Las ${cotizadas} cotizadas entraron con su orden en la carga inicial: no hay nada que medir todavía`
              : `Mediana de ${cotizadas} · la más lenta, ${duracion(demora_maxima_horas)}`
          }
        />

        <Indicador
          rotulo="En precierre"
          valor={String(enPrecierre)}
          alDia={enPrecierre === 0}
          tono={enPrecierre === 0 ? 'ok' : tonoPorEspera(porCerrar)}
          detalle={
            enPrecierre > 0
              ? 'Terminadas o vencidas: falta revisarlas para que salga el acta'
              : `Espera del cliente: ${cliente_mediana_dias ?? '—'} días`
          }
        />
      </div>

      <DondeEstaElEquipo />

      {!hayAlgoPendiente && (
        <p className={estilos.alDia}>
          <span className={estilos.alDiaMarca} aria-hidden="true">
            ✓
          </span>
          <b>Al día:</b> nada esperando su decisión.
        </p>
      )}

      <div className={hayAlgoPendiente ? estilos.dosColumnas : undefined}>
        {hayAlgoPendiente && (
          <Tarjeta
            titulo="Esperando su decisión"
            subtitulo="Casi todo se resuelve aquí mismo; lo que hay que revisar, lleva a su documento"
            tono={hayQueDecidir ? 'viva' : 'normal'}
          >
            <DecisionesRapidas />
          </Tarjeta>
        )}

        <Tarjeta
          titulo="Cómo va el equipo"
          subtitulo="Horas, puntualidad y retrasos · toque una tarjeta para ver sus órdenes"
        >
          <EquipoEnCifras />
        </Tarjeta>
      </div>

      <CifrasDelPeriodo />
    </div>
  )
}
